<template>
  <main class="home">
    <div class="home__container">
      <!-- Bienvenida -->
      <HomeWelcomeHeader :nombre="$store.nombrePilaCliente" />

      <!-- Banner de app + Centro de gestión del estado de cuenta / Imagen lateral -->
      <div class="home__grid">
        <div class="home__grid-main">
          <HomeAppBanner :banners="banners" />

          <HomeStatementCard
            v-model:contrato-seleccionado="contratoSeleccionado"
            :contratos="listaContratos"
            :fecha-limite="fechaLimite"
            :total-pagar="totalPagar"
            :referencia="referencia"
            :link-descarga="linkDescargaComplemento"
            @abrir-aclaraciones="abrirAclaraciones"
          />
        </div>

        <aside class="home__grid-aside">
          <img :src="asideImage" alt="" class="home__aside-image">
        </aside>
      </div>

      <!-- Todo sobre tu financiamiento -->
      <HomeFinancingSection />
    </div>

    <!-- Contacto: DsContactHelp ya aporta su propio fondo y ancho completo -->
    <DsContactHelp />

    <HomeAclaracionesModal
      :aclaraciones="aclaracionesState"
      @update:descripcion="aclaracionesState.descripcion.value = $event"
    />

    <HomeTerminosModal :abierto="mostrarTerminos" @aceptar="aceptarTerminos" />
  </main>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import Swal from 'sweetalert2'
import api from '@/servicios/apiCliente'
import { useStore } from '../../store'

import DsContactHelp from '../MaterialApoyo/components/DsContactHelp.vue'
import HomeWelcomeHeader from './HomeWelcomeHeader.vue'
import HomeAppBanner from './HomeAppBanner.vue'
import HomeStatementCard from './HomeStatementCard.vue'
import HomeFinancingSection from './HomeFinancingSection.vue'
import HomeAclaracionesModal from './HomeAclaracionesModal.vue'
import HomeTerminosModal from './HomeTerminosModal.vue'
import { useEstadoCuenta } from './composables/useEstadoCuenta'
import { useAclaraciones } from './composables/useAclaraciones'

const $store = useStore()
const $router = useRouter()

const aclaracionesState = useAclaraciones()
const mostrarTerminos = ref(false)

const asideImage = new URL('../../assets/home/img_aside.png', import.meta.url).href

const {
  listaContratos,
  contratoSeleccionado,
  banners,
  fechaLimite,
  totalPagar,
  referencia,
  linkDescargaComplemento,
  cargar
} = useEstadoCuenta()

/** Invitación al programa Recicla, una vez por sesión. */
async function invitarProgramaRecicla() {
  const resp = await api.Cliente.Cliente.ProgramaReclicaApi.ObtenerClienteProgramaRecicla()
  const cliente = resp.data.data
  const yaMostrado = Boolean(window.sessionStorage.getItem('invitacionProgramaReciclaMostrado'))
  if (cliente.programaRecicla || yaMostrado) return

  window.sessionStorage.setItem('invitacionProgramaReciclaMostrado', 'true')
  const { isConfirmed } = await Swal.fire({
    html: `<p class="fs-4 fw-bold">Comprometidos con el medio ambiente</p></br>
           <p class="h6">Súmate a nuestro programa Recicla y recibe tu estado de cuenta de forma digital.</p>`,
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Quiero participar',
    cancelButtonText: 'Ahora no',
    width: '35%'
  })

  if (isConfirmed) $router.push({ name: 'cliente-cliente-programa-recicla' })
}

/** Aviso de mensualidades atrasadas. */
async function avisarPagosVencidos() {
  const pagosVencidos = (await api.Cliente.Cliente.HomeApi.ObtenerAdeudoVencidos()).data.data as any
  if (!pagosVencidos?.tienePagosVencidos?.length) return

  Swal.fire({
    html: `<p class="fs-2 fw-bold">Estimado cliente:</p></br>
           <p class="h6 fw-bold">Presentas mensualidades atrasadas de tu financiamiento realiza tu pago y ponte al corriente.</p></br>
           <p class="h6">Recuerda que por el momento no tienes la cobertura de tu seguro de vida o daños.</p>`,
    showCloseButton: true,
    showConfirmButton: false
  })
}

async function aceptarTerminos() {
  await api.Cliente.Cliente.HomeApi.AceptarTerminosCondicionesAvisoPrivacidad()
  mostrarTerminos.value = false
}

onMounted(async () => {
  await invitarProgramaRecicla()
  await avisarPagosVencidos()

  const informacionHome = await cargar()
  $store.mostrarEscrituracion = informacionHome.mostrarEscrituracion
  $store.porLiquidar = informacionHome.porLiquidar

  await aclaracionesState.cargarTiposAclaracion()

  if (!$store.clienteAceptaTerminosCondicionesAvisoPrivacidad) {
    mostrarTerminos.value = true
  }
})

function abrirAclaraciones() {
  aclaracionesState.abrir()
}
</script>

<style scoped>
.home {
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding-top: 40px;
}

/* Figma: 962px de contenido; el padding se suma por fuera. */
.home__container {
  width: 80%;
  margin: 0 auto;
  padding: 0 24px;
}

.home > .home__container > * + * {
  margin-top: 40px;
}

.home__grid {
  display: grid;
  grid-template-columns: 553fr 385fr;
  gap: 24px;
  align-items: stretch;
}

.home__grid-main {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.home__grid-aside {
  border-radius: 16px;
  overflow: hidden;
}

/*
  El asset es horizontal (1535x1024) con la persona a la izquierda. El Figma
  lo encuadra con left:-57.94% / width:305.48% sobre un marco de 385x754,
  equivalente a object-position 28.2%; con `cover` centrado se recortaría
  la cara.
*/
.home__aside-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 28.2% center;
  display: block;
}

.home__contact {
  background: var(--ds-purple-50);
  padding: 32px 0;
}

@media (max-width: 900px) {
  .home__grid {
    grid-template-columns: 1fr;
  }

  .home__grid-aside {
    display: none;
  }
}

@media (max-width: 650px) {
  .home__container{
    width: 100%;
  }
}
</style>
